/**
 * <content-chips>
 *
 * Convierte un texto separado por un caracter (por defecto "|") en pildoras
 * con icono, por ejemplo:
 *
 *   "Caja de 160 pañales | 4 Paquetes de 40 pañales cada uno"
 *   -> [ 📦 Caja de 160 pañales ] [ ▤ 4 Paquetes de 40 pañales cada uno ]
 *
 * El texto original se conserva intacto en el DOM (solo se oculta), porque
 * otros scripts del tema lo reescriben por variante buscando '.prd-block-desc > p'.
 * Cada vez que ese texto cambia, las pildoras se regeneran solas.
 *
 * Si el JS falla o el texto no trae separador, se muestra el parrafo normal.
 */
// Los atributos de presentacion van en el propio SVG para que los iconos se
// vean correctos aunque la hoja de estilos no haya cargado todavia.
const SVG_ATTRS =
  'class="pack-chip__icon" viewBox="0 0 24 24" width="18" height="18" ' +
  'fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"';

const CHIP_ICONS = {
  box:
    '<svg ' + SVG_ATTRS + '>' +
    '<path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Z"/>' +
    '<path d="m3 7.5 9 4.5 9-4.5M12 12v9"/>' +
    '<path d="M7.5 5.25 16.5 9.75"/>' +
    '</svg>',
  stack:
    '<svg ' + SVG_ATTRS + '>' +
    '<path d="M12 2.5 21 7l-9 4.5L3 7l9-4.5Z"/>' +
    '<path d="m3 12 9 4.5L21 12"/>' +
    '<path d="m3 16.75 9 4.5 9-4.5"/>' +
    '</svg>',
};

/** Sobre este largo asumimos que es prosa, no un dato, y no se convierte. */
const MAX_CHIP_LENGTH = 70;

class ContentChips extends HTMLElement {
  connectedCallback() {
    this.separator = this.dataset.separator || '|';
    this.source = this.querySelector('[data-chips-source]');
    this.out = this.querySelector('[data-chips-out]');

    if (!this.source || !this.out) return;

    this.render();

    this.observer = new MutationObserver(() => {
      if (this.busy) return;
      this.render();
    });

    this.observer.observe(this.source, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  /** Texto de origen: el primer parrafo, o todo el bloque si no hay parrafos. */
  readText() {
    const paragraph = this.source.querySelector('p');
    const raw = paragraph ? paragraph.textContent : this.source.textContent;
    return (raw || '').replace(/\s+/g, ' ').trim();
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }

  /** Resalta los numeros para que el conteo se lea de un vistazo. */
  emphasizeNumbers(text) {
    return this.escape(text).replace(/(\d[\d.,]*)/g, '<strong>$1</strong>');
  }

  render() {
    const text = this.readText();
    const parts = text
      .split(this.separator)
      .map((p) => p.trim())
      .filter(Boolean);

    const isChippable =
      parts.length >= 2 && parts.every((p) => p.length <= MAX_CHIP_LENGTH);

    if (!isChippable) {
      this.out.innerHTML = '';
      this.source.classList.remove('is-chipped');
      return;
    }

    const chips = parts.map((part, index) => {
      const icon = index === 0 ? CHIP_ICONS.box : CHIP_ICONS.stack;
      const primary = index === 0 ? ' pack-chip--primary' : '';

      return (
        '<span class="pack-chip' + primary + '">' +
        icon +
        '<span class="pack-chip__text">' + this.emphasizeNumbers(part) + '</span>' +
        '</span>'
      );
    });

    this.busy = true;
    this.out.innerHTML = chips.join('');
    this.source.classList.add('is-chipped');

    // Deja pasar un frame para no reaccionar a nuestra propia escritura.
    window.requestAnimationFrame(() => {
      this.busy = false;
    });
  }
}

if (!customElements.get('content-chips')) {
  customElements.define('content-chips', ContentChips);
}
